import { CommonModule } from '@angular/common';
import {
  Component,
  EventEmitter,
  HostListener,
  Input,
  Optional,
  Output,
  inject,
} from '@angular/core';
import { RouterModule } from '@angular/router';
import { NgbActiveModal, NgbPopoverModule } from '@ng-bootstrap/ng-bootstrap';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Observable, map } from 'rxjs';

import {
  AccountMenuItem,
  User,
  accountMenuItems,
} from '@digital-platform/rs-auth/domain';
import { IdentityService } from '@digital-platform/rs-auth/services';
import { UserMetadata } from '@digital-platform/rs-classic/domain';
import {
  ButtonComponent,
  RichTextLinkComponent,
} from '@digital-platform/rs-cms/feature-ptypes';
import { IconComponent } from '@digital-platform/rs-shared/ui-brand';

@UntilDestroy()
@Component({
  selector: 'digital-platform-account-dropdown-box',
  standalone: true,
  imports: [
    CommonModule,
    NgbPopoverModule,
    RouterModule,
    IconComponent,
    RichTextLinkComponent,
    ButtonComponent,
  ],
  templateUrl: './account-dropdown-box.component.html',
  styleUrls: ['./account-dropdown-box.component.scss'],
})
export class AccountDropdownBoxComponent {
  @Input() public styles: { [key: string]: string } = {};
  @Output() public linkClick = new EventEmitter<null>();

  public identityService = inject(IdentityService);

  public isMobile = false;

  public filteredLoginOptions: AccountMenuItem[] = [];

  public userMetaData$: Observable<UserMetadata | null> =
    this.identityService.user$.pipe(
      map(
        (user: User): UserMetadata | null => user.profile?.user_metadata ?? null
      )
    );

  constructor(@Optional() public activeModal: NgbActiveModal) {
    this.identityService.user$.pipe(untilDestroyed(this)).subscribe({
      next: (user: User | undefined): void => {
        const state: string = user?.state ?? '';

        // Impersonating users are not allowed to see the account menu items
        const isCsr: boolean = user?.profile?.app_metadata?.user_role === 'CSR';

        if (!isCsr) {
          this.filteredLoginOptions = accountMenuItems.filter(
            (option: AccountMenuItem): boolean => option.roles.includes(state)
          );
        }
      },
    });
  }

  @HostListener('window:resize', ['$event'])
  public onResize(event: Event): void {
    if ((event.target as Window).innerWidth > 1200) {
      this.activeModal?.dismiss();
    }
  }

  public logout(): void {
    this.identityService.logout();
    this.activeModal?.dismiss();
    this.linkClick.emit();
  }

  public onLinkClick(): void {
    this.activeModal?.dismiss();
    this.linkClick.emit();
  }

  public changeShowBellIcon(value: boolean): void {
    const match = this.filteredLoginOptions.find(
      (option) => option.title === 'Notifications'
    );

    if (match) {
      match.icon = value ? 'bell-dot-solid' : 'bell-solid';
    }
  }
}
